import { QuestionCircleOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Checkbox,
  Col,
  Form,
  InputNumber,
  Popover,
  Row,
  Slider,
  Space,
  Spin,
} from "antd";
import { ReactComponent as CloseIcon } from "assets/images/trash-icon.svg";
import { useState, useEffect, useRef, useCallback } from "react";
import { fetchCollabsApplicationsEstimations } from "services/SauceService";

export enum EngagementLevel {
  Low = "Low",
  Medium = "Medium",
  High = "High",
}

const COLORS: Record<EngagementLevel, string[]> = {
  [EngagementLevel.Low]: ["#EB5757", "#E0DED9", "#E0DED9"],
  [EngagementLevel.Medium]: ["#F2994A", "#F2994A", "#E0DED9"],
  [EngagementLevel.High]: ["#27AE60", "#27AE60", "#27AE60"],
};

const LABELS: Record<EngagementLevel, string> = {
  [EngagementLevel.Low]: "Low offer",
  [EngagementLevel.Medium]: "OK offer",
  [EngagementLevel.High]: "Good offer",
};

type CompensationProps = {
  name: number;
  key: number;
  fields: any[];
  restField: any;
  remove: (index: number | number[]) => void;
};

const Compensation: React.FC<CompensationProps> = (props) => {
  const { name, key, fields, restField, remove } = props;
  const form = Form.useFormInstance();
  const [level, setLevel] = useState<EngagementLevel>(EngagementLevel.High);
  const [applicationAmount, setApplicationAmount] = useState<number>(5);
  const [engagementDescription, setEngagementDescription] =
    useState<string>("");
  const [visibleFoodComp, setVisibleFoodComp] = useState<boolean>(false);
  const [visibleAverageCheck, setVisibleAverageCheck] =
    useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  const fetchEngagementMetrics = async (values: any) => {
    try {
      setLoading(true);
      const params = {
        ...values,
        addressCoordinates: values.venue.addressCoordinates,
        address: values.venue.address,
      };
      const response: any = await fetchCollabsApplicationsEstimations(params);
      if (response) {
        setLevel(response.applicationsLikelihood);
        setApplicationAmount(response.applicationsAmount);
        setEngagementDescription(response.applicationsDescription);
      }
    } catch (error) {
      console.error("Failed to fetch engagement metrics:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleValueChange = useCallback(() => {
    const { foodCompAmount, cashCompAmount, ...values } =
      form.getFieldValue(["spotsConfig", name]) || {};
    const venue = form.getFieldValue("venue");

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    debounceTimeout.current = setTimeout(() => {
      fetchEngagementMetrics({
        ...values,
        venue,
        foodCompAmount: visibleFoodComp ? foodCompAmount : 0,
        cashCompAmount: visibleAverageCheck ? cashCompAmount : 0,
      });
      form.setFieldsValue({
        spotsConfig: {
          [name]: {
            ...values,
            foodCompAmount: visibleFoodComp ? foodCompAmount : 0,
            cashCompAmount: visibleAverageCheck ? cashCompAmount : 0,
          },
        },
      });
    }, 500);
  }, [form, name, visibleFoodComp, visibleAverageCheck]);

  useEffect(() => {
    const initialValues = form.getFieldValue(["spotsConfig", name]) || {};
    const venue = form.getFieldValue("venue");
    fetchEngagementMetrics({ ...initialValues, venue });
    setVisibleFoodComp(!!initialValues.foodCompAmount);
    setVisibleAverageCheck(!!initialValues.cashCompAmount);

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [form, name]);

  useEffect(() => {
    handleValueChange();
  }, [handleValueChange]);

  return (
    <div className="compensation-card">
      <Card key={key} bordered>
        <Spin spinning={loading}>
          <Space direction="vertical" style={{ width: "100%" }}>
            <Space
              style={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Form.Item
                {...restField}
                name={[name, "minFollowersAmount"]}
                label={
                  <span className="label-gap">
                    Min followers
                    <Popover
                      content={
                        <>
                          <b>How it works:</b> Set the minimum number of
                          followers an influencer must have to apply.
                          <br />
                          💡<b>Tip:</b> We recommend 2,000+ for local nano &
                          micro-creators with high engagement.
                        </>
                      }
                    >
                      <QuestionCircleOutlined />
                    </Popover>
                  </span>
                }
                rules={[
                  {
                    required: true,
                    message: "Required",
                  },
                ]}
              >
                <InputNumber
                  placeholder="Enter amount"
                  onChange={handleValueChange}
                  formatter={(value) =>
                    value
                      ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      : ""
                  }
                  parser={(value) => (value ? value.replace(/,/g, "") : "")}
                  style={{ width: "100%" }}
                />
              </Form.Item>
              <Form.Item
                {...restField}
                name={[name, "maxFollowersAmount"]}
                label={
                  <span className="label-gap">
                    Max followers{" "}
                    <Popover
                      content={
                        <>
                          <b>How it works:</b> Set an upper limit to create a
                          follower range for your offer.
                          <br />
                          <b>Pro move:</b> Create multiple offers with different
                          follower ranges and and comps offers to maximize your
                          reach across influencer types.
                        </>
                      }
                    >
                      <QuestionCircleOutlined />
                    </Popover>
                  </span>
                }
              >
                <InputNumber
                  placeholder="Enter amount"
                  onChange={handleValueChange}
                  formatter={(value) =>
                    value
                      ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      : ""
                  }
                  parser={(value) => (value ? value.replace(/,/g, "") : "")}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Space>

            <Form.Item
              name={[name, "maxMilesDistance"]}
              label="Creators max distance (miles)"
            >
              <Slider
                min={10}
                max={100}
                tooltip={{
                  open: false,
                }}
                marks={{
                  10: "10",
                  20: "20",
                  30: "30",
                  50: "50",
                  80: "80",
                  100: "100",
                }}
                onChange={handleValueChange}
              />
            </Form.Item>

            <Row gutter={16} align="middle">
              <Col flex="auto">
                <Form.Item>
                  <Checkbox
                    checked={visibleFoodComp}
                    onChange={(e) => {
                      setVisibleFoodComp(e.target.checked);
                    }}
                  >
                    <span className="label-gap">
                      Food
                      <Popover
                        content={
                          <>
                            <b>How it works:</b> Mustard issues a food voucher
                            to creators 12 hours before their visit. You'll only
                            be notified once they confirm attendance.
                            <br />
                            💡 <b>Tip:</b> We recommend a food value equal to 2x
                            your average check. This helps attract quality
                            creators.
                          </>
                        }
                      >
                        <QuestionCircleOutlined />
                      </Popover>
                    </span>
                    <span className="ant-form-item-extra">
                      Offer food in exchange for posts (recommended).
                    </span>
                  </Checkbox>
                </Form.Item>
                {visibleFoodComp && (
                  <Form.Item
                    {...restField}
                    name={[name, "foodCompAmount"]}
                    style={{
                      marginTop: 8,
                    }}
                  >
                    <InputNumber
                      placeholder="150"
                      prefix="$"
                      onChange={handleValueChange}
                    />
                  </Form.Item>
                )}
                <Form.Item>
                  <Checkbox
                    checked={visibleAverageCheck}
                    onChange={(e) => {
                      setVisibleAverageCheck(e.target.checked);
                    }}
                  >
                    <span className="label-gap">
                      Comp
                      <Popover
                        content={
                          <>
                            <b>How it works:</b> This amount goes into escrow
                            and is only released once the influencer completes
                            all the instructions.
                          </>
                        }
                      >
                        <QuestionCircleOutlined />
                      </Popover>
                    </span>
                    <span className="ant-form-item-extra">
                      Add optional cash compensation.
                    </span>
                  </Checkbox>
                </Form.Item>
                {visibleAverageCheck && (
                  <Form.Item
                    {...restField}
                    name={[name, "cashCompAmount"]}
                    style={{
                      marginTop: 8,
                    }}
                  >
                    <InputNumber
                      placeholder="100"
                      prefix="$"
                      onChange={handleValueChange}
                    />
                  </Form.Item>
                )}
              </Col>
            </Row>
            <Row>
              <Col>
                <Form.Item
                  name={[name, "guestAllowed"]}
                  noStyle
                  valuePropName="checked"
                >
                  <Checkbox onChange={handleValueChange}>
                    <span className="label-gap">
                      <b>+1 guest allowed</b>{" "}
                      <span
                        style={{
                          color: "#999999",
                        }}
                      >
                        {" "}
                        (recommended)
                      </span>
                      <Popover
                        content={
                          <>
                            <b>How it works:</b> Let creators bring a friend
                            <br />
                            💡 <b>Tip:</b> it increases applications.
                          </>
                        }
                      >
                        <QuestionCircleOutlined />
                      </Popover>
                    </span>
                  </Checkbox>
                </Form.Item>
              </Col>
              <Col
                style={{
                  flex: 1,
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                {fields.length > 1 && (
                  <Button icon={<CloseIcon />} onClick={() => remove(name)} />
                )}
              </Col>
            </Row>
          </Space>
        </Spin>
      </Card>
      <div className="extra-info" style={{ marginBottom: 24 }}>
        <div
          style={{
            flex: 1,
            display: "flex",
            gap: 16,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div style={{ display: "flex", gap: "4px" }}>
              {COLORS[level]?.map((color, index) => (
                <span
                  key={index}
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: color,
                  }}
                />
              ))}
            </div>
            <span>{LABELS[level]}</span>
          </div>

          <div
            style={{
              display: "flex",
              gap: "8px",
              alignItems: "center",
            }}
          >
            &asymp; {applicationAmount} expected applications:{" "}
            {engagementDescription}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Compensation;
